import React from 'react';
import { TitleText, BodyText, BodyTextSmall } from './common';
import { ContinueWithAppleButton } from '../components/ContinueWithAppleButton';
import { ContinueWithEmailButton } from '../components/ContinueWithEmailButton';
import { ContinueWithFacebookButton } from '../components/ContinueWithFacebookButton';
import { ContinueWithGoogleButton } from '../components/ContinueWithGoogleButton';
import routes from 'js/core_pre_webpack/routes';
import { Options as ClickTrackingOptions } from 'js/utils/trackClick';
import styled, { stylesheet } from 'astroturf/react';
import { VerticalSpacer } from '../components/VerticalSpacer';
import { ModalError } from '../components/ModalError';
import {
  ButtonsPageConfigurableText,
  RegistrationParams,
  ModalPage,
} from '../types';

type Props = {
  onAttempt?: () => void;
  onAuthorized?: () => void;
  onSuccess?: (redirect?: string) => void;
  onError: (error?: { errorMessage?: string; errorCodes?: string[] }) => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  errorMessage?: string;
  onEmailButtonClick: () => void;
  show: boolean;
  clickTrackingContext?: Partial<ClickTrackingOptions>;
  text: ButtonsPageConfigurableText;
  registrationParams: RegistrationParams;
  setPage: React.Dispatch<React.SetStateAction<ModalPage>>;
};

const ButtonStyleContext = styled.div<{ show: boolean }>`
  @import 'stylesheets/design_system/tokens';
  @include token_mode(Rebrand);

  display: none;
  flex-direction: column;

  &.show {
    display: flex;
  }
`;

const SharedButtonStyles = stylesheet`
  .margins {
    margin-top: 8px;
    margin-bottom: 8px;
    position: relative; // so the icons can be absolutely positioned
  }

  .margins img {
    position: absolute;
    left: 24px;
    bottom: 20px;
    height: 24px;
  }
`;

const StyledTitleText = styled(TitleText)<{ $withThumbnail: boolean }>`
  &.withThumbnail {
    padding-bottom: 0;
  }
`;

export const ButtonsPage: React.FC<Props> = (props) => {
  const {
    onAttempt,
    onSuccess,
    onError,
    loading,
    setLoading,
    errorMessage,
    onEmailButtonClick,
    show,
    clickTrackingContext,
    text,
    registrationParams,
    setPage,
  } = props;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userInChina = (window as any).userInChina === 'true';

  // Feature flag for disabling Facebook login - hardcoded to true for testing
  const facebookLoginDisabled =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).facebookLoginDisabled === 'true';

  const handleFacebookButtonClick = () => {
    if (facebookLoginDisabled) {
      // Navigate to the facebook-disabled page
      setPage('facebook-disabled');
    }
    // If not disabled, the ContinueWithFacebookButton handles its own click logic
  };

  return (
    <ButtonStyleContext show={show}>
      <StyledTitleText>{text.header}</StyledTitleText>
      <BodyText>{text.subheader}</BodyText>
      <ModalError errorMessage={errorMessage} show={Boolean(errorMessage)} />
      <VerticalSpacer height={32} />
      <BodyTextSmall>
        By clicking Continue, you agree to our{' '}
        <a href={routes.terms_path()}>Terms of Use</a> and{' '}
        <a href={routes.privacy_path()}>Privacy Policy</a>
      </BodyTextSmall>
      <VerticalSpacer height={16} />
      {!userInChina && (
        <ContinueWithGoogleButton
          onAttempt={onAttempt}
          onError={onError}
          onSuccess={onSuccess}
          disabled={loading}
          className={SharedButtonStyles.margins}
          setLoading={setLoading}
          clickTrackingContext={clickTrackingContext}
          buttonText={text.googleButton}
          registrationParams={registrationParams}
        />
      )}
      <ContinueWithAppleButton
        onAttempt={onAttempt}
        onError={onError}
        onSuccess={onSuccess}
        disabled={loading}
        className={SharedButtonStyles.margins}
        setLoading={setLoading}
        clickTrackingContext={clickTrackingContext}
        buttonText={text.appleButton}
        registrationParams={registrationParams}
      />
      {!userInChina &&
        (facebookLoginDisabled ? (
          // When Facebook login is disabled, render a button that looks the same but navigates to the disabled page
          <ContinueWithFacebookButton
            onClick={handleFacebookButtonClick}
            onError={onError}
            onSuccess={onSuccess}
            disabled={loading}
            className={SharedButtonStyles.margins}
            setLoading={setLoading}
            clickTrackingContext={clickTrackingContext}
            buttonText={text.facebookButton}
            registrationParams={registrationParams}
          />
        ) : (
          // Normal Facebook OAuth flow
          <ContinueWithFacebookButton
            onAttempt={onAttempt}
            onError={onError}
            onSuccess={onSuccess}
            disabled={loading}
            className={SharedButtonStyles.margins}
            setLoading={setLoading}
            clickTrackingContext={clickTrackingContext}
            buttonText={text.facebookButton}
            registrationParams={registrationParams}
          />
        ))}
      <ContinueWithEmailButton
        onClick={onEmailButtonClick}
        disabled={loading}
        className={SharedButtonStyles.margins}
        buttonText={text.emailButton}
        userInChina={userInChina}
      />
    </ButtonStyleContext>
  );
};
